import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  Input,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  toast,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { CUSTOM_INTEGRATIONS, MCPIntegrationType, TeammatesApplicableFor } from "./constants";
import { CommonIntegrationInstance } from "./mcpSlice";
import { DatamatesTracking } from "@/lib/tracking/datamates";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createNewCustomIntegration, updateCustomIntegration } from "@/helpers/apis";
import { AutoPlusIcon } from "@assets/icons";
import { useMemo, useState } from "react";
import { InputParameter } from "./constants";
import ToolInputParameterList from "./ToolInputParameterList";
import CustomField from "./CustomField";
import { GET_DATAMATES_CUSTOM_INTEGRATIONS } from "@/pages/query/constants";
import McpConfigImporter from "./McpConfigImporter";
import { FieldErrors } from "react-hook-form";
import ToolField from "./ToolField";

interface CommonIntegrationFormProps {
  integration: (typeof CUSTOM_INTEGRATIONS)[0];
  initialConfig?: CommonIntegrationInstance;
  onClose: () => void;
}

// Create a dynamic schema based on integration config
const createFormSchema = (integration: (typeof CUSTOM_INTEGRATIONS)[0]) => {
  return z
    .object({
      name: z.string().min(1, "Name is required"),
      description: z.string().optional(),
      tools: z
        .array(
          z.object({
            key: z.string(),
            description: z.string(),
            name: z.string(),
          })
        )
        .default([]),
    })
    .extend(
      integration.toolConfig.reduce(
        (acc, config) => {
          acc[config.key] = config.schema;
          return acc;
        },
        {} as Record<string, z.ZodSchema>
      )
    );
};

const CustomIntegrationForm = ({
  integration,
  initialConfig,
  onClose,
}: CommonIntegrationFormProps) => {
  const [savedInputs, setSavedInputs] = useState<InputParameter[]>(initialConfig?.inputs || []);
  const [savedConfigs, setSavedConfigs] = useState<InputParameter[]>(
    initialConfig?.config || integration.config || []
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const queryClient = useQueryClient();
  // Dynamic schema with user configs
  const formSchema = useMemo(() => {
    let schema = createFormSchema(integration);
    return schema;
  }, [integration]);

  type FormData = z.infer<typeof formSchema>;

  const { register, handleSubmit, reset, setValue, watch, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...initialConfig?.toolConfig?.reduce(
        (acc, cfg) => {
          if (cfg.key !== undefined && "value" in cfg) {
            acc[cfg.key] = cfg.value;
          }
          return acc;
        },
        {} as Record<string, unknown>
      ),
      tools: initialConfig?.tools,
      name: initialConfig?.name || "",
      description: initialConfig?.description || "",
    },
  });

  const { mutate } = useMutation({
    mutationFn: async (instance: Partial<CommonIntegrationInstance>) => {
      if (!initialConfig?.id) {
        return await createNewCustomIntegration(instance);
      }
      return await updateCustomIntegration(initialConfig?.id, instance);
    },
    onSuccess: async () => {
      if (initialConfig?.id) {
        DatamatesTracking.customIntegrationEdited(initialConfig.id);
      } else {
        DatamatesTracking.customIntegrationCreated(integration.type);
      }
      toast({
        title: `Integration ${initialConfig ? "updated" : "created"} successfully`,
      });
      await queryClient.invalidateQueries({ queryKey: [GET_DATAMATES_CUSTOM_INTEGRATIONS] });
      await queryClient.invalidateQueries({
        queryKey: [`FILTER_${TeammatesApplicableFor}`],
      });
    },
    onError: err => {
      toast({
        title: `Error saving ${watch("name")}: ${err}`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: FormData) => {
    const newInstance: Partial<CommonIntegrationInstance> = {
      type: integration.type,
      ...data,
      name: data.name,
      description: data.description || "",
      inputs: savedInputs,
      config: savedConfigs,
      tools: data.tools,
      toolConfig: (integration.toolConfig || []).map(cfg => ({
        key: cfg.key,
        value: data[cfg.key],
      })),
    };
    await mutate(newInstance);
    reset();
    onClose();
  };

  const onServerSelected = (server: Partial<CommonIntegrationInstance>) => {
    // update values in form
    Object.entries(server).forEach(([key, value]) => {
      switch (key) {
        case "config":
          setSavedConfigs(value as InputParameter[]);
          break;
        default:
          setValue(key, value);
      }
    });
  };

  const tools = watch("tools");

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="!al-w-1/2 !al-max-w-[700px] al-pr-0 al-pt-0">
        <div className="al-relative">
          <form onSubmit={handleSubmit(onSubmit)} className="al-p-2">
            <SheetHeader>
              <SheetTitle>
                <Stack align="start" justify="between">
                  <div className="al-overflow-hidden al-text-nowrap al-text-ellipsis">
                    {initialConfig ? "Edit" : "Configure"} {integration.name}
                  </div>
                </Stack>
              </SheetTitle>
            </SheetHeader>
            <Stack direction="column" className="al-gap-4 al-mt-6">
              {integration.type === MCPIntegrationType.MCP && (
                <McpConfigImporter
                  onServerSelected={onServerSelected}
                  isEditing={!!initialConfig}
                />
              )}
              <Stack direction="column" className="al-gap-2">
                <label htmlFor="integration-name-field">
                  Name
                  <div>
                    <Input
                      id="integration-name-field"
                      {...register("name")}
                      placeholder="Enter integration name"
                    />
                    {formState.errors.name && (
                      <p className="al-text-sm al-text-destructive">
                        {formState.errors.name.message?.toString()}
                      </p>
                    )}
                  </div>
                </label>
              </Stack>

              <Stack direction="column" className="al-gap-2">
                <label htmlFor="integration-description-field">
                  Description
                  <div>
                    <Textarea
                      id="integration-description-field"
                      {...register("description")}
                      placeholder="Enter integration description"
                      className="al-h-[100px]"
                    />
                    {formState.errors.description && (
                      <p className="al-text-sm al-text-destructive">
                        {formState.errors.description.message?.toString()}
                      </p>
                    )}
                  </div>
                </label>
              </Stack>

              {integration.configureInputs && (
                <Stack direction="column" className="al-gap-2">
                  <Stack direction="row" className="al-justify-between al-items-center">
                    <h3 className="al-text-sm al-font-medium">Input Parameters</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSavedInputs(prev => {
                          const newInputs = [
                            ...prev,
                            {
                              key: "",
                              name: "",
                              type: "string",
                              description: "",
                              required: false,
                              items: { type: "string" },
                            },
                          ];
                          setTimeout(() => setEditingIndex(newInputs.length - 1), 0);
                          return newInputs;
                        });
                      }}
                    >
                      <AutoPlusIcon className="al-w-4 al-h-4 al-mr-2" />
                      Add Input
                    </Button>
                  </Stack>
                  <p className="al-text-sm al-text-gray-500">
                    Specified input parameters will be passed to the tool from LLM.
                  </p>
                  <ToolInputParameterList
                    inputs={savedInputs}
                    onChange={inputs => {
                      if (
                        editingIndex !== null &&
                        inputs.length < savedInputs.length &&
                        editingIndex >= inputs.length
                      ) {
                        setEditingIndex(null);
                      }
                      setSavedInputs(inputs);
                    }}
                    editingIndex={editingIndex}
                    setEditingIndex={setEditingIndex}
                  />
                </Stack>
              )}

              {integration.configureConfig && (
                <Card>
                  <CardContent className="al-pt-4">
                    <Stack direction="column">
                      <Stack justify="between" align="center">
                        <h3 className="al-text-sm al-font-medium">Connection Configuration</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSavedConfigs(prev => {
                              const newConfigs = [
                                ...prev,
                                {
                                  key: "",
                                  name: "",
                                  type: "string",
                                  description: "",
                                  required: false,
                                  items: { type: "string" },
                                },
                              ];
                              setTimeout(() => setEditingIndex(newConfigs.length - 1), 0);
                              return newConfigs;
                            });
                          }}
                        >
                          <AutoPlusIcon className="al-w-4 al-h-4 al-mr-2" />
                          Add Config
                        </Button>
                      </Stack>
                      <p className="al-text-sm al-text-gray-500">
                        Specified config parameters will be used for configuring connections to this
                        tool.
                      </p>
                      <ToolInputParameterList
                        inputs={savedConfigs}
                        onChange={configs => {
                          if (
                            editingIndex !== null &&
                            configs.length < savedConfigs.length &&
                            editingIndex >= configs.length
                          ) {
                            setEditingIndex(null);
                          }
                          setSavedConfigs(configs);
                        }}
                        editingIndex={editingIndex}
                        setEditingIndex={setEditingIndex}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {integration.toolConfig?.map(c => (
                <CustomField
                  key={c.key}
                  config={c}
                  register={register}
                  formState={formState}
                  watch={watch}
                  setValue={setValue}
                />
              ))}

              {tools?.length ? (
                <ToolField
                  configKey={"tools"}
                  configLabel={"Tools"}
                  register={register}
                  formState={formState}
                  watch={watch}
                  setValue={setValue}
                  registerPrefix={""}
                />
              ) : null}
            </Stack>

            <SheetFooter className="al-mt-[1px] al-sticky al-bottom-0 al-bg-background al-py-4">
              <div>
                {formState.isSubmitted && Object.keys(formState.errors).length > 0 && (
                  <div className="al-my-4 al-p-3 al-border al-border-destructive al-rounded al-bg-destructive/10">
                    <Stack direction="column" className="al-gap-2">
                      <p className="al-text-sm al-text-destructive">
                        There are errors in the form. Please review and correct them.
                      </p>
                      <Accordion type="single" collapsible className="al-w-full">
                        <AccordionItem value="error-details">
                          <AccordionTrigger>
                            <Button type="button" variant="outline" size="sm" className="al-w-fit">
                              View Errors
                            </Button>
                          </AccordionTrigger>
                          <AccordionContent>
                            <pre className="al-whitespace-pre-wrap al-text-sm al-max-w-full al-overflow-auto al-mt-2 al-p-2 al-border al-border-destructive al-rounded">
                              {(() => {
                                // Define a type for nested error structures
                                type NestedError =
                                  | { message?: string }
                                  | { [key: string]: NestedError };

                                const formatErrors = (
                                  errors: FieldErrors<FormData> | Record<string, NestedError>,
                                  path: string = ""
                                ): string[] => {
                                  let messages: string[] = [];
                                  for (const key in errors) {
                                    if (Object.prototype.hasOwnProperty.call(errors, key)) {
                                      const currentPath = path ? `${path} -> ${key}` : key;
                                      const errorValue = errors[key] as NestedError;
                                      if (
                                        errorValue &&
                                        typeof errorValue === "object" &&
                                        "message" in errorValue &&
                                        typeof errorValue.message === "string"
                                      ) {
                                        messages.push(`${currentPath}: ${errorValue.message}`);
                                      } else if (
                                        errorValue &&
                                        typeof errorValue === "object" &&
                                        !("message" in errorValue)
                                      ) {
                                        messages = messages.concat(
                                          formatErrors(
                                            errorValue as Record<string, NestedError>,
                                            currentPath
                                          )
                                        );
                                      }
                                    }
                                  }
                                  return messages;
                                };
                                const errorMessages = formatErrors(formState.errors).join("\n");
                                return (
                                  errorMessages ||
                                  "No specific error messages found. Please check fields."
                                );
                              })()}
                            </pre>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </Stack>
                  </div>
                )}
                <Stack direction="row" className="al-gap-2 al-w-full">
                  <Button type="button" variant="outline" className="al-flex-1" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" className="al-flex-1">
                    {initialConfig ? "Update" : "Save"}
                  </Button>
                </Stack>
              </div>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CustomIntegrationForm;
